import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { Chip, Table, TableBody, TableCell, TableRow } from "@mui/material";

import {
  ALERT_PROTOCOL_TABLE_HEADER,
  OPERATOR_DISPLAY_MAP,
} from "static/alert_protocol";
import { capitalizeFirstLetter } from "utils/string";
import {
  ContentCopyOutlined,
  DeleteOutline,
  EditOutlined,
} from "@mui/icons-material";
import ConfirmDeleteModal from "components/Modal/ConfirmDeleteModal/ConfirmDeleteModal";
import { useDeleteAlertProtocolMutation } from "app/store/alertProtocol/api/alertProtocolApi";
import {
  setSelectedContent,
  setIsEditModalOpen,
  setIsEditMode,
  setIsCopyMode,
} from "app/store/alertProtocol/slice/alertProtocolSlice";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { makeStyles } from "@mui/styles";
import EmptyContainer from "components/common/empty_container/EmptyContainer";

const useStyles = makeStyles(() => ({
  subsectionCell: {
    px: 1,
    py: 0.5,
    textAlign: "left",
  },
  occurrenceCell: {
    px: 1,
    py: 0.5,
    textAlign: "left",
  },
  metricsCell: {
    px: 1,
    py: 0.5,
    textAlign: "center",
  },
  alertTypeCell: {
    width: "15%",
    px: 1,
    py: 0.5,
    textAlign: "center",
  },
  actionsCell: {
    width: "12%",
    px: 1,
    py: 0.5,
    textAlign: "center",
  },
  paddingCell: {
    width: "11%",
  },
  chip: {
    fontSize: "0.7rem",
    height: "22px",
  },
  centeredCell: {
    textAlign: "center",
  },
}));

const AlerttProtocolTable = ({ alertProtocols }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProtocolId, setSelectedProtocolId] = useState(null);
  const [deleteAlertProtocol, { isLoading }] = useDeleteAlertProtocolMutation();

  const getConditionValue = (conditions, metric) => {
    if (!conditions || !Array.isArray(conditions)) return "-";
    const condition = conditions.find((c) => c?.metric === metric);
    if (!condition) return "-";
    return `${OPERATOR_DISPLAY_MAP[condition?.operator] || condition?.operator || ""} ${condition?.value || ""} ${
      condition?.unit || ""
    }`.trim();
  };

  const getProtocolType = (id) => id?.toLowerCase?.() || "";

  const getHeadersForProtocol = (protocolType) => {
    const baseStart = ["Subsection", "Occurrence"];

    if (protocolType === "battery") {
      return [...baseStart, "Duration", "Status"];
    } else if (protocolType === "arrhythmia") {
      return [...baseStart, "Duration", "Rate", "Count", "OAC", "LAAO"];
    } else if (protocolType === "af burden") {
      return [...baseStart, "% Change", "LAAO"];
    }

    return ALERT_PROTOCOL_TABLE_HEADER;
  };

  const handleDelete = (id) => {
    setSelectedProtocolId(id);
    setIsDeleteModalOpen(true);
  };

  const handleEdit = (content) => {
    dispatch(setSelectedContent(content));
    dispatch(setIsEditMode(true));
    dispatch(setIsEditModalOpen(true));
  };

  const onConfirmDelete = async () => {
    try {
      const response = await deleteAlertProtocol(selectedProtocolId).unwrap();

      if (!response?.error) {
        showSuccessToast(response?.message);
        setIsDeleteModalOpen(false);
        setSelectedProtocolId(null);
      }
    } catch (error) {
      showErrorToast(error?.message);
    }
  };

  const handleCopy = (content) => {
    if (!content) {
      showErrorToast("Unable to copy protocol content");
      return;
    }

    const copiedContent = {
      ...content,
    };

    dispatch(setSelectedContent(copiedContent));
    dispatch(setIsCopyMode(true));
    dispatch(setIsEditModalOpen(true));
  };

  return (
    <div className="preferences-table">
      {alertProtocols?.length === 0 ? (
        <EmptyContainer
          noDataText={
            <div style={{ textAlign: "center" }}>
              <h3 className="empty__title">Alert protocol not available</h3>
              <p
                className="empty__content"
                style={{ maxWidth: "475px", margin: "0 auto" }}
              >
                No preferences are currently configured for the selected
                account. <br /> Please add an alert protocol for this account.
              </p>
            </div>
          }
        />
      ) : (
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableBody>
            {alertProtocols?.map((protocol, protocolIndex) => {
              const protocolType = getProtocolType(protocol?._id);

              return (
                <React.Fragment key={protocolIndex}>
                  <TableRow
                    sx={{
                      backgroundColor: "#ECECEC",
                      "& td": {
                        fontSize: "16px",
                        lineHeight: "20px",
                        fontWeight: 500,
                      },
                    }}
                  >
                    <TableCell colSpan={12} sx={{ px: 1 }}>
                      {protocol?._id
                        ? capitalizeFirstLetter(protocol?._id)
                        : "-"}
                    </TableCell>
                  </TableRow>

                  <TableRow
                    sx={{
                      "& td": { fontWeight: 600, px: 1, py: 1 },
                    }}
                  >
                    {getHeadersForProtocol(protocolType).map(
                      (header, index) => {
                        const isMetricsColumn = [
                          "Duration",
                          "Rate",
                          "Count",
                          "Status",
                          "% Change",
                          "OAC",
                          "LAAO",
                        ].includes(header);

                        return (
                          <TableCell
                            key={index}
                            className={
                              isMetricsColumn ? classes.centeredCell : ""
                            }
                          >
                            {header}
                          </TableCell>
                        );
                      }
                    )}
                    {protocolType === "battery" && (
                      <>
                        <TableCell sx={classes.paddingCell} />
                        <TableCell sx={classes.paddingCell} />
                        <TableCell sx={classes.paddingCell} />
                        <TableCell sx={classes.paddingCell} />
                        <TableCell sx={classes.paddingCell} />
                      </>
                    )}
                    {protocolType === "af burden" && (
                      <>
                        <TableCell sx={classes.paddingCell} />
                        <TableCell sx={classes.paddingCell} />
                        <TableCell sx={classes.paddingCell} />
                        <TableCell sx={classes.paddingCell} />
                        <TableCell sx={classes.paddingCell} />
                      </>
                    )}
                    {protocolType === "arrhythmia" && (
                      <>
                        <TableCell sx={classes.paddingCell} />
                        <TableCell sx={classes.paddingCell} />
                      </>
                    )}
                    <TableCell>Alert Type</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>

                  {protocol?.content?.map((content, contentIndex) => {
                    const subSection =
                      content?.sub_section || content?.note_section;

                    const durationValue = getConditionValue(
                      content?.conditions,
                      "duration"
                    );

                    const rateValue = getConditionValue(
                      content?.conditions,
                      "rate"
                    );

                    const countValue = getConditionValue(
                      content?.conditions,
                      "count"
                    );

                    const statusValue = getConditionValue(
                      content?.conditions,
                      "status"
                    );

                    const percentageChangeValue = getConditionValue(
                      content?.conditions,
                      "percentage_change"
                    );

                    return (
                      <TableRow key={contentIndex}>
                        <TableCell sx={classes.subsectionCell}>
                          {subSection ? capitalizeFirstLetter(subSection) : "-"}
                        </TableCell>

                        <TableCell sx={classes.occurrenceCell}>
                          {content?.occurrence
                            ? capitalizeFirstLetter(content?.occurrence)
                            : "-"}
                        </TableCell>

                        {["arrhythmia", "battery"].includes(protocolType) && (
                          <TableCell className={classes.metricsCell}>
                            {durationValue !== "-" ? (
                              <Chip
                                label={durationValue}
                                size="small"
                                className={classes.chip}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        )}

                        {protocolType === "arrhythmia" && (
                          <>
                            <TableCell className={classes.metricsCell}>
                              {rateValue !== "-" ? (
                                <Chip
                                  label={rateValue}
                                  size="small"
                                  className={classes.chip}
                                />
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell className={classes.metricsCell}>
                              {countValue !== "-" ? (
                                <Chip
                                  label={countValue}
                                  size="small"
                                  className={classes.chip}
                                />
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          </>
                        )}

                        {protocolType === "battery" && (
                          <TableCell className={classes.metricsCell}>
                            {statusValue !== "-" ? (
                              <Chip
                                label={statusValue}
                                size="small"
                                className={classes.chip}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        )}

                        {protocolType === "af burden" && (
                          <TableCell className={classes.metricsCell}>
                            {percentageChangeValue !== "-" ? (
                              <Chip
                                label={percentageChangeValue}
                                size="small"
                                className={classes.chip}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        )}

                        {protocolType === "arrhythmia" && (
                          <TableCell className={classes.metricsCell}>
                            {Object?.hasOwn(content, "oac_status") ? (
                              <Chip
                                label={
                                  content?.oac_status
                                    ? (() => {
                                        const status = content.oac_status
                                          .split(",")[0]
                                          .trim()
                                          .toLowerCase();
                                        return (
                                          status.charAt(0).toUpperCase() +
                                          status.slice(1)
                                        );
                                      })()
                                    : "-"
                                }
                                size="small"
                                className={classes.chip}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        )}

                        {["arrhythmia", "af burden"].includes(protocolType) && (
                          <TableCell className={classes.metricsCell}>
                            {Object?.hasOwn(content, "laao_status") ? (
                              <Chip
                                label={
                                  content?.laao_status === true ? "Yes" : "No"
                                }
                                size="small"
                                className={classes.chip}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        )}

                        {protocolType === "battery" && (
                          <>
                            <TableCell sx={classes.paddingCell} />
                            <TableCell sx={classes.paddingCell} />
                            <TableCell sx={classes.paddingCell} />
                            <TableCell sx={classes.paddingCell} />
                            <TableCell sx={classes.paddingCell} />
                          </>
                        )}

                        {protocolType === "af burden" && (
                          <>
                            <TableCell sx={classes.paddingCell} />
                            <TableCell sx={classes.paddingCell} />
                            <TableCell sx={classes.paddingCell} />
                            <TableCell sx={classes.paddingCell} />
                            <TableCell sx={classes.paddingCell} />
                          </>
                        )}

                        {protocolType === "arrhythmia" && (
                          <>
                            <TableCell sx={classes.paddingCell} />
                            <TableCell sx={classes.paddingCell} />
                          </>
                        )}

                        <TableCell sx={classes.alertTypeCell}>
                          {Object?.hasOwn(content, "alert_type") ? (
                            <Chip
                              label={content?.alert_type}
                              color={
                                content?.alert_type === "Warning"
                                  ? "warning"
                                  : "error"
                              }
                              size="small"
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell sx={classes.actionsCell}>
                          <ContentCopyOutlined
                            onClick={() => handleCopy(content)}
                            sx={{ cursor: "pointer", fontSize: 16, mr: 1 }}
                          />
                          <EditOutlined
                            onClick={() => handleEdit(content)}
                            sx={{
                              cursor: "pointer",
                              mr: 1,
                              fontSize: 18,
                            }}
                          />
                          <DeleteOutline
                            onClick={() => handleDelete(content?._id)}
                            sx={{ cursor: "pointer", fontSize: 18 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}

      <ConfirmDeleteModal
        onDelete={onConfirmDelete}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedProtocolId(null);
        }}
        isModalOpen={isDeleteModalOpen}
        isLoading={isLoading}
        modalTitleText="Delete Protocol"
        modalDescriptionText="Are you sure you want to delete this protocol?"
      />
    </div>
  );
};

export default AlerttProtocolTable;
